import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { IQuestionnaire } from '../../types/questionnaire';
import { FormBuilderService } from '../../api/FormBuilderService';
import { getDecodedToken } from '../../utils/tokenHelper';
import Spinner from '../Spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import useResponsive from '../../hooks/useResponsive';
import './QuestionnaireAttachmentDrawer.css';
import { useLayout } from '../../context/LayoutContext';

interface QuestionnaireAttachmentDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  jobId: number;
  jobTitle: string;
  currentQuestionnaireId?: string | null;
  onQuestionnaireAttached?: () => void;
  isLoadingCurrentQuestionnaire?: boolean; // New prop to indicate if job-questionnaire check is in progress
  // VI-579 follow-up (Bug 1c): when the user mutates a job-questionnaire link from this
  // job-centric drawer on the Jobs tab, fire this callback so the Questionnaires-tab
  // table can decrement the previous questionnaire's count and/or increment the new one's
  // optimistically. Without this the table caches stale values until the user opens the
  // affected row's View / Reactivate modal (which corrects via Bug 3's reconciliation).
  onActiveJobsCountChange?: (
    questionnaireId: string,
    change: { delta?: number; absolute?: number }
  ) => void;
}

interface ConfirmationModalProps {
  isOpen: boolean;
  title: string;
  message: string | React.ReactNode;
  confirmButtonText?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  title,
  message,
  confirmButtonText = 'Proceed',
  onConfirm,
  onCancel
}) => {
  if (!isOpen) return null;

  return (
    <div className="modal-backdrop-attachment" onClick={onCancel}>
      <div className="modal-dialog-attachment" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content-attachment">
          <div className="modal-header-attachment">
            <h5 className="modal-title-attachment">{title}</h5>
            <button
              type="button"
              className="close-button-attachment"
              onClick={onCancel}
            >
              &times;
            </button>
          </div>
          <div className="modal-body-attachment">
            {typeof message === 'string' ? (
              message.split('\n\n').map((paragraph, index) => (
                <p key={index} style={{ marginBottom: index === 0 ? '1rem' : '0' }}>{paragraph}</p>
              ))
            ) : (
              message
            )}
          </div>
          <div className="modal-footer-attachment">
            <button className="btn-cancel-attachment" onClick={onCancel}>
              Cancel
            </button>
            <button className="btn-proceed-attachment" onClick={onConfirm}>
              {confirmButtonText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const QuestionnaireAttachmentDrawer: React.FC<QuestionnaireAttachmentDrawerProps> = ({
  isOpen,
  onClose,
  jobId,
  jobTitle,
  currentQuestionnaireId,
  onQuestionnaireAttached,
  isLoadingCurrentQuestionnaire = false,
  onActiveJobsCountChange,
}) => {
  const navigate = useNavigate();
  const { setHeaderContent } = useLayout();
  const isMobile = useResponsive('md');
  const [questionnaires, setQuestionnaires] = useState<IQuestionnaire[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState<string | null>(
    currentQuestionnaireId || null
  );
  const [previewQuestionnaire, setPreviewQuestionnaire] = useState<IQuestionnaire | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalConfig, setModalConfig] = useState<{
    title: string;
    message: string | React.ReactNode;
    confirmButtonText: string;
    onConfirm: () => void;
  } | null>(null);
  const [alert, setAlert] = useState<{
    isOpen: boolean;
    message: string;
    type: 'success' | 'detach' | 'error';
  }>({
    isOpen: false,
    message: '',
    type: 'success'
  });
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (isOpen && jobId) {
      fetchQuestionnaires();
    }
  }, [isOpen, jobId]);

  useEffect(() => {
    // Update selected questionnaire when currentQuestionnaireId changes
    setSelectedQuestionnaireId(currentQuestionnaireId || null);
    
    // Update preview if we have questionnaires loaded
    if (currentQuestionnaireId && questionnaires.length > 0) {
      const current = questionnaires.find(q => q.questionnaireId === currentQuestionnaireId);
      if (current) {
        setPreviewQuestionnaire(current);
      }
    } else {
      setPreviewQuestionnaire(null);
    }
  }, [currentQuestionnaireId, questionnaires]);

  useEffect(() => {
    if (alert.isOpen) {
      const timer = setTimeout(() => {
        setAlert({ ...alert, isOpen: false });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [alert.isOpen]);

  const fetchQuestionnaires = async () => {
    // Don't fetch if we don't have a valid jobId
    if (!jobId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const decodedToken = getDecodedToken();
      const companyId = decodedToken?.companyId;

      if (!companyId) {
        console.error('No company ID found');
        return;
      }

      const data = await FormBuilderService.getQuestionnairesByCompanyId(companyId.toString());
      setQuestionnaires(data);
      //setQuestionnaires([]);

      // Set preview if current questionnaire exists
      if (currentQuestionnaireId) {
        const current = data.find(q => q.questionnaireId === currentQuestionnaireId);
        if (current) {
          setPreviewQuestionnaire(current);
        }
      }
    } catch (error) {
      console.error('Error fetching questionnaires:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionnaireSelect = (questionnaireId: string | null) => {
    // Only update preview - no automatic attach/detach
    if (questionnaireId === null) {
      setPreviewQuestionnaire(null);
    } else {
      const questionnaire = questionnaires.find(q => q.questionnaireId === questionnaireId);
      setPreviewQuestionnaire(questionnaire || null);
    }
  };

  const handleAttachQuestionnaire = async (questionnaireId: string, isSwitch: boolean = false) => {
    try {
      setIsProcessing(true);
      setShowConfirmModal(false);
      
      // Get questionnaire name for the success message
      const questionnaire = questionnaires.find(q => q.questionnaireId === questionnaireId);
      const questionnaireName = questionnaire?.questionnaireName || 'Questionnaire';
      
      // Update preview when proceeding with attachment
      setPreviewQuestionnaire(questionnaire || null);
      
      if(isSwitch) {
        await FormBuilderService.detachQuestionnaireFromJob(jobId, currentQuestionnaireId || undefined);
        // VI-579 follow-up (Bug 1c): if this is a swap, the OLD questionnaire just lost this
        // job — tell the questionnaires table to decrement its count.
        if (currentQuestionnaireId) {
          onActiveJobsCountChange?.(currentQuestionnaireId, { delta: -1 });
        }
      }
      await FormBuilderService.associateQuestionnaireWithJob(jobId, questionnaireId);

      // VI-579 follow-up (Bug 1c): the NEW questionnaire just gained this job.
      onActiveJobsCountChange?.(questionnaireId, { delta: +1 });
      
      setAlert({
        isOpen: true,
        message: `${questionnaireName} has been attached to ${jobTitle}`,
        type: 'success'
      });

      // Call callback to update parent component
      if (onQuestionnaireAttached) {
        onQuestionnaireAttached();
      }

      // Close drawer after a short delay
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (error) {
      console.error('Error attaching questionnaire:', error);
      setAlert({
        isOpen: true,
        message: 'Failed to attach questionnaire. Please try again.',
        type: 'error'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDetachQuestionnaire = async () => {
    try {
      setIsProcessing(true);
      setShowConfirmModal(false);
      
      // Get the name of the questionnaire being detached
      const detachedQuestionnaire = questionnaires.find(q => q.questionnaireId === currentQuestionnaireId);
      const questionnaireName = detachedQuestionnaire?.questionnaireName || 'Questionnaire';
      
      // Update preview when proceeding with detachment
      setPreviewQuestionnaire(null);
      setSelectedQuestionnaireId(null);
      
      await FormBuilderService.detachQuestionnaireFromJob(jobId, currentQuestionnaireId || undefined);

      // VI-579 follow-up (Bug 1c): notify the questionnaires table that this questionnaire
      // lost a job link, so the cached "Jobs Attached" count decrements without waiting for
      // the user to navigate over and re-open a modal.
      if (currentQuestionnaireId) {
        onActiveJobsCountChange?.(currentQuestionnaireId, { delta: -1 });
      }
      
      setAlert({
        isOpen: true,
        message: `${questionnaireName} has been detached from ${jobTitle}`,
        type: 'detach'
      });

      // Call callback to update parent component
      if (onQuestionnaireAttached) {
        onQuestionnaireAttached();
      }

      // Close drawer after a short delay
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (error) {
      console.error('Error detaching questionnaire:', error);
      setAlert({
        isOpen: true,
        message: 'Failed to detach questionnaire. Please try again.',
        type: 'error'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCreateQuestionnaire = () => {
    setHeaderContent(null);
    console.log('Creating questionnaire');
    navigate('questionnaire-builder');
  };

  const handleQuestionnaireChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const questionnaireId = value === 'none' ? null : value;
    
    // Update the selected radio button (for UI state)
    setSelectedQuestionnaireId(questionnaireId);
    
    // Handle the selection logic (which may show modals)
    // Preview will only update after confirmation or if no confirmation needed
    handleQuestionnaireSelect(questionnaireId);
  };

  // Render left panel content
  const renderLeftPanel = () => {
    if (questionnaires.length === 0) {
      return (
        <div className="d-flex flex-column justify-content-center align-items-center">
          <div className="text-center p-4 border rounded" style={{ backgroundColor: '#f8f9fa', width: '100%' }}>
            <p className="text-muted mb-3">You don't have any questionnaires yet.</p>
            <button className="btn btn-primary" onClick={handleCreateQuestionnaire}>
              Create Questionnaire
            </button>
          </div>
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {/* No Questionnaire option */}
        <div 
          className={`d-flex align-items-center p-2 rounded questionnaire-radio-item${selectedQuestionnaireId === null ? ' selected' : ''}`}
          style={{ 
            border: '1px solid #DEE2E6',
            borderRadius: '8px',
            cursor: 'pointer'
          }}
          onClick={() => !isProcessing && handleQuestionnaireChange({ target: { value: 'none' } } as React.ChangeEvent<HTMLInputElement>)}
        >
          <span style={{ flex: 1, color: selectedQuestionnaireId === null ? 'white' : 'inherit', padding: '4px' }}>
            No Questionnaire
          </span>
          {/* Checkmark only shows if no questionnaire is currently attached */}
          {currentQuestionnaireId === null && (
            <i className="fas fa-circle-check" style={{ color: selectedQuestionnaireId === null ? 'white' : '#2F86DD', fontSize: '13px', marginRight: '4px' }}></i>
          )}
        </div>

        {/* List of questionnaires */}
        {questionnaires.map((questionnaire) => {
          const isSelected = selectedQuestionnaireId === questionnaire.questionnaireId;
          const isCurrentlyAttached = currentQuestionnaireId === questionnaire.questionnaireId;
          return (
            <div 
              key={questionnaire.questionnaireId} 
              className={`d-flex align-items-center p-2 rounded questionnaire-radio-item${isSelected ? ' selected' : ''}`}
              style={{ 
                border: '1px solid #DEE2E6',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
              onClick={() => !isProcessing && handleQuestionnaireChange({ target: { value: questionnaire.questionnaireId } } as React.ChangeEvent<HTMLInputElement>)}
            >
              <span style={{ flex: 1, color: isSelected ? 'white' : 'inherit', padding: '4px' }}>
                {questionnaire.questionnaireName}
              </span>
              {/* Checkmark only shows if this questionnaire is currently attached */}
              {isCurrentlyAttached && (
                <i className="fas fa-circle-check" style={{ color: isSelected ? 'white' : '#2F86DD', fontSize: '13px', marginRight: '4px' }}></i>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Render right panel content
  const renderRightPanel = () => {
    if (!previewQuestionnaire) {
      return (
        <div className="preview-empty-state">
          <img 
            src="/svgs/no-results.svg" 
            alt="No questionnaire" 
            className="preview-empty-image"
          />
          <h5 className="preview-empty-title">No Questionnaire Attached</h5>
          <p className="preview-empty-text">Add a questionnaire from the left to preview questions here.</p>
        </div>
      );
    }

    return (
      <div className="preview-content">
        <div className="preview-header">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '12px' }}>
              <h5 className="preview-header-title" style={{ fontSize: '1.3rem', fontWeight: 500, lineHeight: '24px', letterSpacing: '0%', color: '#181C32', flex: 1, margin: 0, wordBreak: 'break-word' }}>
                {previewQuestionnaire.questionnaireName}
              </h5>
              {previewQuestionnaire.questionnaireId === currentQuestionnaireId ? (
                // Detach button - shown when previewing the currently attached questionnaire
                <button 
                  type="button" 
                  className="btn btn-outline-danger"
                  disabled={isProcessing}
                  style={{ flexShrink: 0, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '4px' }}
                  onClick={() => {
                    setModalConfig({
                      title: 'Detaching Questionnaire',
                      message: `${jobTitle} job will no longer accept screener submissions after detachment.`,
                      confirmButtonText: 'Proceed',
                      onConfirm: () => handleDetachQuestionnaire()
                    });
                    setShowConfirmModal(true);
                  }}
                >
                  {isProcessing ? <Spinner size="sm" /> : <><span>Detach</span> <i className="fal fa-circle-minus" style={{ fontSize: '14px' }}></i></>}
                </button>
              ) : (
                // Attach button - shown when previewing a different questionnaire
                <button 
                  type="button" 
                  className="btn btn-outline-primary"
                  disabled={isProcessing}
                  style={{ flexShrink: 0, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '4px' }}
                  onClick={() => {
                    if (currentQuestionnaireId) {
                      // Switching from one questionnaire to another
                      const currentQuestionnaire = questionnaires.find(q => q.questionnaireId === currentQuestionnaireId);
                      const fromName = currentQuestionnaire?.questionnaireName || 'current questionnaire';
                      const toName = previewQuestionnaire.questionnaireName;
                      setModalConfig({
                        title: 'Confirm Switch',
                        message: (
                          <>
                            <p style={{ fontWeight: 400, marginBottom: '1rem' }}>
                              You're updating the questionnaire for {jobTitle} from “<span style={{ fontWeight: 600 }}>{fromName}</span>“ to “<span style={{ fontWeight: 600 }}>{toName}</span>“.
                            </p>
                            <p style={{ fontWeight: 400, marginBottom: 0 }}>
                              This change applies only to future screeners. Screeners that are already completed will not be updated.
                            </p>
                          </>
                        ),
                        confirmButtonText: 'Confirm',
                        onConfirm: () => handleAttachQuestionnaire(previewQuestionnaire.questionnaireId, true)
                      });
                      setShowConfirmModal(true);
                    } else {
                      // No current questionnaire, attach directly
                      handleAttachQuestionnaire(previewQuestionnaire.questionnaireId, false);
                    }
                  }}
                >
                  {isProcessing ? <Spinner size="sm" /> : <><span>Attach</span> <i className="fal fa-check" style={{ fontSize: '14px' }}></i></>}
                </button>
              )}
            </div>
            <span style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', cursor: 'default' }}>
            <i className={`fal ${previewQuestionnaire.contentType === 'Video' ? 'fa-video' : 'fa-microphone'}`} style={{ fontSize: '16px', marginRight: '4px', color: '#495057' }}></i>
              <p className="text-muted ml-1" style={{ marginBottom: '0', color: '#6C757D', fontWeight: 400 }}> {previewQuestionnaire.contentType} </p>
            </span>
                
            {/* <img 
              src={previewQuestionnaire.contentType?.toLowerCase() === 'video' ? '/svgs/video.svg' : '/svgs/mic.svg'} 
              alt={previewQuestionnaire.contentType} 
              className="preview-header-icon"
              style={{ width: '20px', height: '20px' }}
            /> */}
          </div>
        </div>
        
        <div className="preview-questions">
          {previewQuestionnaire.questions && previewQuestionnaire.questions.length > 0 ? (
            previewQuestionnaire.questions.map((question, index) => (
              <div key={question.id || index} className="preview-question-item">
                <p className="preview-question-text">
                  {index + 1}. {question.questionText || question.text}
                </p>
                {question.type !== 'text' && question.options && question.options.length > 0 && (
                  <ul className="preview-question-options">
                    {question.options.map((option, optIndex) => (
                      <li key={optIndex} className="preview-question-option">
                        <span className="preview-option-bullet">•</span>
                        {option.label}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))
          ) : (
            <p className="text-muted">No questions available</p>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1040,
          display: isOpen ? 'block' : 'none'
        }}
        onClick={onClose}
      />

      {/* Drawer Panel */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: isMobile ? '100%' : '50%',
          height: '100%',
          backgroundColor: 'white',
          boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.15)',
          transition: 'transform 0.3s ease-in-out',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          zIndex: 1050,
          display: 'flex',
          flexDirection: 'column',
          overflowX: 'hidden',
          borderTopLeftRadius: isMobile ? '0' : '12px',
          borderBottomLeftRadius: isMobile ? '0' : '12px'
        }}
      >
        {/* Header */}
        <div style={{ 
          padding: isMobile ? '0.75rem' : '1rem', 
          borderBottom: '1px solid #e9ecef',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h4 style={{ margin: 0, fontSize: isMobile ? '1.1rem' : '1.25rem', cursor: 'default' }}>
            {currentQuestionnaireId ? "Manage Questionnaire" : "Attach Questionnaire"}
          </h4>
          <FontAwesomeIcon 
            icon={faTimes} 
            style={{ 
              fontSize: isMobile ? '1.5rem' : '1.2rem', 
              color: '#6c757d', 
              cursor: 'pointer',
              padding: isMobile ? '0.5rem' : '0.2rem'
            }}
            onClick={onClose}
          />
        </div>

        {/* Content Area */}
        <div style={{ 
          flex: 1,
          display: 'flex',
          overflow: isMobile ? 'auto' : 'hidden',
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          {/* Centered Loading Spinner */}
          {(loading || isLoadingCurrentQuestionnaire) ? (
            <div style={{ 
              flex: 1, 
              display: 'flex', 
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '300px'
            }}>
              <Spinner size="xl" />
            </div>
          ) : (
            <>
              {/* Left Panel */}
              <div style={{
                flex: isMobile ? '0 0 auto' : 0.6,
                minHeight: isMobile ? '300px' : 'auto',
                maxHeight: isMobile ? '50vh' : 'none',
                borderRight: isMobile ? 'none' : '1px solid #e9ecef',
                borderBottom: isMobile ? '1px solid #e9ecef' : 'none',
                display: 'flex',
                flexDirection: 'column'
              }}>
                {/* Scrollable Content */}
                <div style={{
                  flex: 1,
                  overflowY: 'auto',
                  padding: isMobile ? '0.75rem' : '1rem',
                  paddingRight: isMobile ? '0.75rem' : '1rem',
                  paddingBottom: '1rem',
                  WebkitOverflowScrolling: 'touch'
                }}>
                  <div style={{ marginBottom: '1.4rem', fontFamily: 'Poppins', fontWeight: 500, fontSize: '17.5px', lineHeight: '24px', letterSpacing: '0%', color: '#6C757D', cursor: 'default' }}>
                    Screener Questionnaires
                  </div>
                  {renderLeftPanel()}
                </div>
              </div>

              {/* Right Panel */}
              <div style={{
                flex: 1,
                minHeight: isMobile ? '400px' : 'auto',
                padding: isMobile ? '0.75rem' : '1rem 1rem 1rem 1.5rem',
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch'
              }}>
                {renderRightPanel()}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {modalConfig && (
        <ConfirmationModal
          isOpen={showConfirmModal}
          title={modalConfig.title}
          message={modalConfig.message}
          confirmButtonText={modalConfig.confirmButtonText}
          onConfirm={modalConfig.onConfirm}
          onCancel={() => {
            setShowConfirmModal(false);
            // Reset selected questionnaire to current
            setSelectedQuestionnaireId(currentQuestionnaireId || null);
            if (currentQuestionnaireId) {
              const current = questionnaires.find(q => q.questionnaireId === currentQuestionnaireId);
              setPreviewQuestionnaire(current || null);
            } else {
              setPreviewQuestionnaire(null);
            }
          }}
        />
      )}

      {/* Toast Notification */}
      {alert.isOpen && (
        <div className="toast-notification-container">
          <div 
            className={`toast-notification ${
              alert.type === 'success' ? 'toast-success' : 
              alert.type === 'error' ? 'toast-error' : 'toast-detach'
            }`}
          >
            {/* Icon */}
            <i className={`fal ${alert.type === 'success' ? 'fa-circle-check' : alert.type === 'error' ? 'fa-xmark' : 'fa-circle-minus'}`} style={{ fontSize: '20px', marginRight: '10px', color: alert.type === 'success' ? '#09423F' : alert.type === 'error' ? '#dc3545' : '#343A40' }}></i>
            
            {/* Message */}
            <p className="toast-message mb-0">
              {alert.message}
            </p>
            
            {/* Close button */}
            <button 
              type="button" 
              onClick={() => setAlert({ ...alert, isOpen: false })}
              className="toast-close-button"
              onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default QuestionnaireAttachmentDrawer;

